/**
 * Sanitizes individual image URLs to handle malformed URLs from V1/V2 of the app
 *
 * Fixes URLs that have duplicate S3 bucket paths like:
 * "https://s3-us-west-2.amazonaws.com/bassmollett-caseimages/https://bassmollett-caseimages.s3.us-west-2.amazonaws.com/..."
 *
 * This can happen when URL construction logic incorrectly prepends bucket URLs
 * to paths that are already complete URLs.
 */

import { regEx } from '@/utils/global/regEx'
import { logger, EventCategory } from '@/utils/logger'
import { loggerSentry } from '@/utils/loggerSentry'

/**
 * Sanitizes a single malformed image URL by extracting the correct URL
 * from a duplicated S3 bucket path structure
 *
 * in V1/V2 of the app, urls have become malformed sometimes, leading to varying url states (i.e. just the path or the full url)
 * so this is a bandaid solution to correct the url until we can clean them all in the DB
 */
export const sanitizeImageUrl = (
  imageUrl: string | null | undefined,
): string | null | undefined => {
  // Return early if URL is falsy or not a string
  const imageUrlIsFalsyOrNotAString = !imageUrl || typeof imageUrl !== 'string'
  if (imageUrlIsFalsyOrNotAString) {
    const warningMessage =
      'Invalid image URL input provided to sanitizeImageUrl'
    const logData = {
      message: warningMessage,
      category: EventCategory.ERROR,
      data: {
        function: 'sanitizeImageUrl',
        issue: 'invalid_input',
        providedImageUrl: imageUrl,
        imageUrlType: typeof imageUrl,
        imageUrlIsFalsy: !imageUrl,
      },
    }
    logger.warn(logData)
    loggerSentry.warn(logData)
    return imageUrl
  }

  // Check if URL contains duplicate S3 bucket paths using centralized regex
  const urlHasDuplicateBucketPath = regEx.duplicateS3BucketPath.test(imageUrl)

  if (urlHasDuplicateBucketPath) {
    // Extract the second (correct) URL from the malformed URL structure
    const secondUrlMatch = imageUrl.match(
      regEx.extractSecondUrlFromDuplicatedPath,
    )
    if (secondUrlMatch && secondUrlMatch[1]) {
      const cleanedImageUrl = secondUrlMatch[1]
      const infoMessage = 'Malformed image URL detected and cleaned'
      const logData = {
        message: infoMessage,
        category: EventCategory.DISCREPANCY,
        data: {
          function: 'sanitizeImageUrl',
          issue: 'malformed_url_cleaned',
          originalImageUrl: imageUrl,
          cleanedImageUrl: cleanedImageUrl,
          urlContainsDuplicatePath: true,
        },
      }
      logger.info(logData)
      loggerSentry.info(logData)
      return cleanedImageUrl
    }
  }

  // Return URL unchanged if no malformation detected
  return imageUrl
}
